import { partialMatchKey } from "@tanstack/react-query";
import type { NavigateOptions } from "@tanstack/react-router";
import { redirect } from "@tanstack/react-router";
import {
  formOptionsQueryOptions,
  organizationQueryOptions,
  type Task,
  tasksQueryOptions,
} from "@/lib/data";
import { ensureConsumerQueryData, queryClient } from "@/lib/query-client";
import { clearReferral } from "@/lib/referral";
import {
  resolveTaskJourneyTarget,
  taskJourneyTarget,
} from "@/lib/task-progress";

// Applicants signed in by an older build keep their session, so this key and
// the plain-token format must not change.
const TOKEN_KEY = "approvehub_token";

export const PLAID_LINK_TOKEN_STORAGE_KEY = "plaid_link_token";
// The task a Plaid OAuth round trip started from, so /plaid-links can return to it.
export const PLAID_LINK_TASK_STORAGE_KEY = "plaid_link_task_id";
// The bank being repaired in update mode, so /plaid-links reconnects it
// instead of linking a new one.
export const PLAID_LINK_RECONNECT_STORAGE_KEY = "plaid_link_reconnect_id";

/** The ky `context` for requests that establish a session. */
export const SIGN_IN_REQUEST_CONTEXT = { signIn: true } as const;

const endedListeners = new Set<() => void>();

function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Whether a credential is stored. Reads storage on every call and isn't
 * reactive. That's enough because every change to the session is followed by
 * a navigation (sign-in navigates; sign-out and a rejected credential signal
 * the root to go home), so components that read it re-render.
 */
export function isSignedIn(): boolean {
  return getStoredToken() !== null;
}

/**
 * Subscribes to the session ending for any reason. Returns an unsubscribe
 * function.
 */
export function onSessionEnded(listener: () => void): () => void {
  endedListeners.add(listener);
  return () => {
    endedListeners.delete(listener);
  };
}

/**
 * Drops every cached query except the Organization's. That one depends only on
 * the page's domain, never on the Applicant, so keeping it leaks nothing and
 * spares the root a full-screen loader while it refetches.
 */
function purgeApplicantQueries() {
  queryClient.removeQueries({
    predicate: (query) =>
      !partialMatchKey(query.queryKey, organizationQueryOptions.queryKey),
  });
}

/** Forgets the previous Applicant's Referral and Plaid resume state. */
function forgetPreviousApplicant() {
  clearReferral();

  if (typeof window !== "undefined") {
    localStorage.removeItem(PLAID_LINK_TOKEN_STORAGE_KEY);
    localStorage.removeItem(PLAID_LINK_TASK_STORAGE_KEY);
    localStorage.removeItem(PLAID_LINK_RECONNECT_STORAGE_KEY);
  }
}

/**
 * Clears the credential and everything kept for the Applicant, then tells
 * the listeners.
 *
 * Does NOT touch the query cache: that would race with any in-flight query
 * triggered from the same 401 response. {@link signOut} purges it.
 */
function endSession() {
  if (typeof window !== "undefined") {
    localStorage.removeItem(TOKEN_KEY);
  }
  forgetPreviousApplicant();

  for (const listener of endedListeners) listener();
}

/**
 * Ends the session on purpose. Purges the Applicant's cached queries first so the next
 * Applicant on a shared browser can't see the previous Applicant's data while
 * fresh queries refetch. Nothing is in flight that matters, unlike a 401.
 */
export function signOut(): void {
  purgeApplicantQueries();
  endSession();
}

/**
 * Sets the bearer credential on a request, unless it is a sign-in request:
 * that establishes a session, so it must not carry (or be judged by) a
 * previous one.
 */
export function authorizeRequest(
  request: Request,
  context: Record<string, unknown>,
): void {
  if (context.signIn === true) return;

  const token = getStoredToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
}

/**
 * Ends the session when the server rejects the stored credential. A 401 to a
 * request that didn't carry it says nothing about the session.
 */
export function endSessionIfRejected(
  request: Request,
  response: Response,
): void {
  if (response.status !== 401) return;

  const token = getStoredToken();
  if (token && request.headers.get("Authorization") === `Bearer ${token}`) {
    endSession();
  }
}

/**
 * Starts a session for an Applicant, who may differ from the previous one,
 * and returns where to send them: their Next task. Purges the Applicant's
 * cached queries first: query keys are not scoped by Applicant, so anything cached under a
 * previous identity (a prior sign-in on a shared device, or a dealer
 * switching between impersonations) would otherwise be served to the new
 * session until it went stale.
 *
 * Doesn't notify the {@link onSessionEnded} listeners: the root sends the
 * Applicant home when they fire, which would race the caller's own
 * navigation. Never rejects; the target falls back to the task list.
 */
export async function startSession(token: string): Promise<NavigateOptions> {
  purgeApplicantQueries();
  localStorage.setItem(TOKEN_KEY, token);
  forgetPreviousApplicant();

  return resolveTaskJourneyTarget();
}

/**
 * The Applicant's tasks, or null when there is no credential or the server
 * rejected it. Resolving the tasks doubles as the session check: a 401 has
 * already ended the session by the time this returns null.
 */
async function validatedSessionTasks(): Promise<Task[] | null> {
  if (!isSignedIn()) return null;

  const data = await ensureConsumerQueryData(tasksQueryOptions);
  return data === null ? null : data.tasks;
}

/**
 * Route guard for pages that need an Applicant. Redirects to `/` when there's
 * no credential or the server no longer accepts it, so protected UI never
 * mounts with a stale token.
 */
export async function requireSession(): Promise<void> {
  const tasks = await validatedSessionTasks();
  if (tasks === null) {
    throw redirect({ to: "/" });
  }

  // Not awaited, and its rejection is swallowed: the query cache already
  // reports the failure to Sentry, and a missing prefetch only means the forms
  // load the options themselves.
  ensureConsumerQueryData(formOptionsQueryOptions).catch(() => {});
}

/**
 * Route guard for the login page. Sends a signed-in Applicant to their Next
 * task.
 */
export async function redirectSignedInApplicant(): Promise<void> {
  const tasks = await validatedSessionTasks();
  // A stale token gets the login form rather than a bounce through /steps.
  if (tasks === null) return;

  throw redirect(taskJourneyTarget(tasks));
}
